/** Iter 639 — vendor panel split · Step 2: KYC document upload (multipart, both docs mandatory)
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, UploadCloud, CheckCircle2, FileText } from "lucide-react";
import { sharedApi } from "@/lib/api";
import {
  ENGAGEMENT_HEADERS, ENGAGEMENT_PARAM, KYC_ACCEPT, MAX_KYC_BYTES,
  REQUIRED_KYC_DOCS, errMsg, fetchVendorMe, Field,
} from "@/features/vendorPanel/helpers";

export default function KycCard({ v }) {
  const { setStep } = v;
  const nextStep = () => setStep(3);
  const onBack = () => setStep(1);
  const qc = useQueryClient();
  const [docType, setDocType] = useState("business_registration_cert");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  // Iter-316 §Bug2 — types uploaded during THIS session (server state below
  // covers refresh/resume). The wizard only advances when BOTH mandatory
  // docs exist, otherwise the application never reaches the review queue.
  const [localDone, setLocalDone] = useState({});

  const vendorQ = useQuery({
    queryKey: ["partner-vendor-me"],
    retry: false,
    queryFn: fetchVendorMe,
  });
  const checklist = vendorQ.data?.kyc_checklist || {};
  const isDocDone = (t) =>
    !!localDone[t] ||
    (t === "owner_photo_id" ? !!checklist.owner_photo_id : false) ||
    (t === "business_registration_cert" ? !!checklist.proof_of_business : false);
  const missingDocs = REQUIRED_KYC_DOCS.filter((d) => !isDocDone(d.type));

  // Default the picker to the first missing mandatory doc once we know
  // the server state (don't fight a manual selection of an extra doc).
  useEffect(() => {
    if (missingDocs.length > 0 && isDocDone(docType)) {
      setDocType(missingDocs[0].type);
    }
  }, [vendorQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Please choose a file");
      if (file.size > MAX_KYC_BYTES) {
        throw new Error("File is too large — please pick one under 8 MB.");
      }
      // Iter-300 §1 — stream the raw file via multipart/form-data so
      // multi-page PDFs don't blow past the ingress body cap (the legacy
      // base64 endpoint 520'd on Cloudflare for >3 MB statements). We
      // deliberately do NOT set Content-Type: the browser fills in the
      // correct `multipart/form-data; boundary=…` header automatically.
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      const { data } = await sharedApi.post(
        "/classifieds/engagement/vendors/me/kyc-docs/upload",
        fd,
        {
          headers: ENGAGEMENT_HEADERS,
          params: ENGAGEMENT_PARAM,
          // Long uploads on flaky mobile need more than the default 20s.
          timeout: 60_000,
        },
      );
      return data;
    },
    onSuccess: (data) => {
      const uploadedType = data?.doc_type || docType;
      const nextDone = { ...localDone, [uploadedType]: true };
      setLocalDone(nextDone);
      setFile(null);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ["partner-vendor-me"] });
      const stillMissing = REQUIRED_KYC_DOCS.filter(
        (d) => !nextDone[d.type] && !isDocDone(d.type),
      );
      if (stillMissing.length === 0) {
        toast.success("Both documents uploaded — we'll review them shortly.");
        nextStep();
      } else {
        setDocType(stillMissing[0].type);
        toast.success(`Document uploaded — now add your ${stillMissing[0].label.toLowerCase()}.`);
      }
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 413) toast.error("That file is over 8 MB — please compress or trim pages.");
      else if (status === 415) toast.error("Only JPG, PNG, WEBP or PDF files are supported.");
      else toast.error(errMsg(err, "Upload failed"));
    },
  });

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f && /^image\//.test(f.type)) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a file to upload.");
      return;
    }
    uploadMut.mutate();
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-4"
      data-testid="partner-kyc-form"
    >
      <h2 className="font-display text-2xl font-black text-slate-900">Verify your business</h2>
      <p className="text-sm text-slate-500">
        Upload BOTH documents below so we can review your application and unlock your 🏢 Verified Business badge and payouts.
      </p>

      {/* Iter-316 §Bug2 — mandatory 2-doc checklist */}
      <ul className="space-y-1.5" data-testid="partner-kyc-required-docs">
        {REQUIRED_KYC_DOCS.map((d) => {
          const done = isDocDone(d.type);
          return (
            <li
              key={d.type}
              data-testid={`partner-kyc-req-${d.type}`}
              className={`flex items-center gap-2 text-sm rounded-xl px-3 py-2 border ${
                done
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-slate-50 border-slate-200 text-slate-600"
              }`}
            >
              {done ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : (
                <span className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" aria-hidden="true" />
              )}
              <span className="font-semibold">{d.label}</span>
              <span className="ml-auto text-[10px] font-bold uppercase tracking-widest">
                {done ? "Uploaded" : "Required"}
              </span>
            </li>
          );
        })}
      </ul>

      <Field label="Document type">
        <select
          data-testid="partner-kyc-doc-type"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className="pp-input"
        >
          <option value="business_registration_cert">Business registration certificate</option>
          <option value="owner_photo_id">Owner photo ID</option>
          <option value="business_licence">Business licence</option>
          <option value="utility_bill">Utility bill (business address)</option>
          <option value="lease_agreement">Lease agreement</option>
          <option value="other">Other</option>
        </select>
      </Field>

      <Field label="Upload file *">
        <label className="flex items-center justify-center gap-2 w-full rounded-2xl border-2 border-dashed border-slate-200 hover:border-[color:var(--brand-blue)] transition-colors px-4 py-6 cursor-pointer bg-slate-50/50">
          <UploadCloud className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-600" data-testid="partner-kyc-file-label">
            {file ? file.name : "Click to choose a PDF or image"}
          </span>
          <input
            type="file"
            accept={KYC_ACCEPT}
            onChange={onPick}
            className="hidden"
            data-testid="partner-kyc-file-input"
          />
        </label>
        {preview && (
          <img
            src={preview}
            alt="Document preview"
            className="mt-3 max-h-48 rounded-xl border border-soft"
            data-testid="partner-kyc-preview"
          />
        )}
      </Field>

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-semibold text-slate-500 hover:text-slate-900"
          data-testid="partner-kyc-back"
        >
          ← Back
        </button>
        <button
          type="submit"
          disabled={uploadMut.isPending || !file}
          data-testid="partner-kyc-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-3 disabled:opacity-70 transition-colors"
        >
          {uploadMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Upload & continue
        </button>
      </div>
    </form>
  );
}
